import React, { useState, useEffect } from "react";
import { Search, Lock, ChevronRight } from "lucide-react";

const AnalyzeButton = ({
    isLoading,
    isLimitReached,
    hasSelection,
    searchLimit,
    mergedLots,
    analysisStep,
    analysisStepText,
    selectedState = "CA",
    onAnalyze
}) => {
    // Disabled state
    const isDisabled = isLoading || (!hasSelection && !isLimitReached);

    // Live elapsed stopwatch timer
    const [seconds, setSeconds] = useState(0);

    useEffect(() => {
        if (!isLoading) {
            setSeconds(0);
            return;
        }

        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsed = (Date.now() - startTime) / 1000;
            setSeconds(elapsed);
        }, 80); // Smooth real-time update

        return () => clearInterval(interval);
    }, [isLoading]);

    const formatTime = (sec) => {
        return sec.toFixed(2) + "s";
    };

    // Format status text dynamically for CA/TX zoning terminologies
    let displayStatusText = analysisStepText || "Initializing...";
    if (displayStatusText === "Calculating MFA/MDA") {
        displayStatusText = selectedState === "TX"
            ? "Calculating Lot Coverage"
            : "Calculating MDA & MFA Limits";
    }

    // Map audit steps to high-tech system categories
    const getCategory = (step) => {
        if (step <= 1) return "GIS CONNECTION";
        if (step <= 3) return "ZONING SCHEMAS";
        if (step <= 5) return "TERRAIN MODEL";
        if (step <= 7) return "GEOMETRY AUDIT";
        return "COMPLIANCE CHECK";
    };

    return (
        <div className="relative group/tooltip w-full">
            {/* Inject custom CSS keyframes for 3D laser scanner sweep */}
            <style>{`
                @keyframes scan-laser-3d {
                    0% { transform: translateY(-4px); opacity: 0.3; }
                    50% { transform: translateY(18px); opacity: 0.9; }
                    100% { transform: translateY(-4px); opacity: 0.3; }
                }
                .laser-line-3d {
                    animation: scan-laser-3d 3.5s ease-in-out infinite;
                }
            `}</style>

            <button
                onClick={onAnalyze}
                disabled={isDisabled}
                className={`w-full rounded-2xl font-semibold text-sm sm:text-base tracking-wide transition-all duration-300 flex flex-col justify-center group relative overflow-hidden active:scale-[0.99] border ${
                    isLimitReached
                        ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200 py-4"
                        : isLoading
                        ? "bg-slate-950 text-white border-transparent cursor-wait shadow-[0_0_35px_rgba(99,102,241,0.25)] px-4 sm:px-6 py-4.5 sm:py-5 min-h-[112px]"
                        : isDisabled
                        ? "bg-slate-50 text-slate-350 border-slate-200 cursor-not-allowed font-medium py-4"
                        : "bg-slate-900 hover:bg-slate-950 text-white border-slate-800 hover:border-indigo-500/50 shadow-md hover:shadow-xl hover:shadow-indigo-500/10 py-4"
                }`}
                title={isLimitReached ? `Free search limit reached (${searchLimit}/${searchLimit})` : ""}
            >
                {/* Premium rotating flowing border on loading */}
                {isLoading && (
                    <div className="absolute inset-0 -z-10 pointer-events-none p-[1.5px]">
                        <div className="absolute inset-[-200%] bg-[conic-gradient(from_0deg,#22d3ee,#6366f1,#a855f7,#22d3ee)] animate-[spin_4s_linear_infinite]" />
                        <div className="absolute inset-[1.5px] bg-slate-950 rounded-2xl" />
                    </div>
                )}

                {/* Premium hover shimmer sweep overlay */}
                {!isDisabled && !isLimitReached && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-shimmer pointer-events-none" style={{ animationDuration: '2s' }}></div>
                )}

                {isLoading ? (
                    <div className="flex flex-col w-full text-left relative z-10 space-y-4">
                        {/* Upper Details Block */}
                        <div className="flex items-center justify-between w-full">
                            {/* Left: 3D Isometric Blueprint + Text Stack */}
                            <div className="flex items-center space-x-4 min-w-0">
                                {/* 3D Isometric Viewport Box */}
                                <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 flex items-center justify-center bg-[#070c1b] rounded-xl border border-blue-900/30 overflow-hidden shadow-inner">
                                    {/* Grid blueprint pattern */}
                                    <div className="absolute inset-0 opacity-15" style={{
                                        backgroundImage: 'radial-gradient(circle, #22d3ee 1px, transparent 1px)',
                                        backgroundSize: '8px 8px'
                                    }}></div>

                                    {/* 3D Model Container with tilt perspective */}
                                    <div className="relative w-12 h-12" style={{
                                        transform: 'perspective(120px) rotateX(45deg) rotateZ(-30deg)',
                                        transformStyle: 'preserve-3d'
                                    }}>
                                        <svg className="absolute inset-0 w-12 h-12" viewBox="0 0 100 100" fill="none">
                                            {/* 1. Ground slice base cube */}
                                            {/* Bottom face */}
                                            <polygon points="50,75 80,60 50,45 20,60" fill="#0b172a" stroke="#1d4ed8" strokeWidth="1" />
                                            {/* Left wall */}
                                            <polygon points="20,60 50,75 50,85 20,70" fill="#060b18" stroke="#1d4ed8" strokeWidth="1" />
                                            {/* Right wall */}
                                            <polygon points="50,75 80,60 80,70 50,85" fill="#080f22" stroke="#1d4ed8" strokeWidth="1" />

                                            {/* 2. 3D Setback Envelope (Fades in at step >= 3) */}
                                            <g className={`transition-all duration-700 ${analysisStep >= 3 ? "opacity-100" : "opacity-15"}`}>
                                                {/* Setback base */}
                                                <polygon points="50,67 74,55 50,43 26,55" fill="none" stroke="#6366f1" strokeWidth="1" strokeDasharray="3 2" />
                                                {/* Setback top extrusion */}
                                                <polygon points="50,47 74,35 50,23 26,35" fill="rgba(99,102,241,0.12)" stroke="#6366f1" strokeWidth="1" />
                                                {/* Vertical columns */}
                                                <line x1="26" y1="55" x2="26" y2="35" stroke="#6366f1" strokeWidth="1" />
                                                <line x1="50" y1="67" x2="50" y2="47" stroke="#6366f1" strokeWidth="1" />
                                                <line x1="74" y1="55" x2="74" y2="35" stroke="#6366f1" strokeWidth="1" />
                                            </g>

                                            {/* 3. 3D Buildable building envelope volume (Fades/rises in at step >= 6) */}
                                            <g className={`transition-all duration-700 ${analysisStep >= 6 ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-6 scale-50"}`}>
                                                {/* Building top */}
                                                <polygon points="50,33 66,25 50,17 34,25" fill="rgba(16,185,129,0.3)" stroke="#10b981" strokeWidth="1.5" />
                                                {/* Building left wall */}
                                                <polygon points="34,25 50,33 50,53 34,45" fill="rgba(16,185,129,0.2)" stroke="#10b981" strokeWidth="1.5" />
                                                {/* Building right wall */}
                                                <polygon points="50,33 66,25 66,45 50,53" fill="rgba(16,185,129,0.25)" stroke="#10b981" strokeWidth="1.5" />
                                            </g>

                                            {/* 4. Sweeping laser plane */}
                                            <g className="laser-line-3d absolute w-full pointer-events-none">
                                                <line x1="5" y1="20" x2="95" y2="20" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" className="blur-[1px]" />
                                            </g>
                                        </svg>
                                    </div>
                                </div>

                                {/* Text Stack */}
                                <div className="min-w-0 flex flex-col justify-center">
                                    <div className="flex items-center space-x-1.5 leading-none">
                                        <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                                        </span>
                                        <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-indigo-400 font-mono">
                                            {getCategory(analysisStep)}
                                        </span>
                                        <span className="text-[8px] sm:text-[9px] font-bold text-slate-500 font-mono">
                                            ({formatTime(seconds)})
                                        </span>
                                    </div>
                                    <div className="text-xs sm:text-sm font-extrabold text-white truncate leading-tight mt-1 tracking-tight">
                                        {displayStatusText}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Percentage */}
                            <div className="flex flex-col items-end justify-center pl-2 flex-shrink-0">
                                <span className="text-2xl sm:text-3xl font-black font-mono tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400 leading-none drop-shadow-[0_0_10px_rgba(34,211,238,0.3)]">
                                    {Math.min(Math.round((analysisStep / 9) * 100), 100)}%
                                </span>
                                <span className="text-[8px] sm:text-[9px] uppercase tracking-widest text-slate-500 font-extrabold mt-1 font-mono">
                                    3D SCANNING
                                </span>
                            </div>
                        </div>

                        {/* Lower Architectural Scale Ruler Progress Stepper */}
                        <div className="flex items-end justify-between gap-1 sm:gap-1.5 w-full">
                            {Array.from({ length: 10 }).map((_, idx) => {
                                const isCompleted = idx < analysisStep;
                                const isActive = idx === analysisStep;
                                // Ruler height marks: end and middle ticks are taller
                                const tickHeight = (idx === 0 || idx === 9) ? "h-3" : (idx === 4 || idx === 5) ? "h-2" : "h-1.5";
                                return (
                                    <div
                                        key={idx}
                                        className={`${tickHeight} flex-1 rounded-full relative overflow-hidden transition-all duration-500 ${
                                            isCompleted
                                                ? "bg-gradient-to-r from-cyan-400 to-indigo-500 shadow-[0_0_8px_rgba(34,211,238,0.7)]"
                                                : "bg-slate-900 border border-slate-800"
                                        }`}
                                    >
                                        {isActive && (
                                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-cyan-400 to-indigo-500 w-[200%] h-full animate-shimmer" style={{ backgroundSize: '50% 100%' }}></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : isLimitReached ? (
                    <div className="flex items-center justify-center space-x-2.5 sm:space-x-3 py-0.5 w-full">
                        <Lock className="w-4 h-4 sm:w-5 h-5 text-slate-450" />
                        <span className="font-semibold">Limit Reached ({searchLimit}/{searchLimit})</span>
                    </div>
                ) : (
                    <div className="flex items-center justify-center space-x-2.5 sm:space-x-3 py-0.5 w-full">
                        <Search className={`w-4 h-4 sm:w-4.5 sm:h-4.5 transition-transform duration-350 ${
                            isDisabled ? "text-slate-350" : "text-indigo-400 group-hover:text-cyan-450 group-hover:scale-110 group-hover:rotate-6"
                        }`} />
                        <span className="font-bold">
                            {mergedLots.length > 0 ? "Start Combined Analysis" : "Start Analysis"}
                        </span>
                        <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${
                            isDisabled ? "text-slate-300" : "text-slate-400 group-hover:text-white group-hover:translate-x-1.5"
                        }`} />
                    </div>
                )}
            </button>

            {isLimitReached && (
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-slate-900 border border-slate-800 text-white text-xs py-1.5 px-3 rounded-lg shadow-xl opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                    Upgrade to Pro for unlimited searches
                </div>
            )}
        </div>
    );
};

export default AnalyzeButton;
